import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  CreateFirmIntakeQuestionInput,
  SubmitFirmClientIntakeAnswersInput,
  UpdateFirmIntakeQuestionInput,
} from '../generated/graphql';
import {
  clientCreateFirmIntakeQuestion,
  clientDeleteFirmIntakeQuestion,
  clientSubmitFirmClientIntakeAnswers,
  clientUpdateFirmIntakeQuestion,
  useGraphqlClient,
} from '../graphql';
import { openNotification } from '../UI';
import { firmIntakeQuestionKeys } from './queryKeys';

export const useCreateFirmIntakeQuestion = (firmID: string | undefined | null) => {
  const queryClient = useQueryClient();
  const gqlClient = useGraphqlClient();
  const { t } = useTranslation('firm');

  const createFirmIntakeQuestion = useMutation({
    mutationFn: (input: CreateFirmIntakeQuestionInput) => clientCreateFirmIntakeQuestion(gqlClient, { input }),
    onError: () => openNotification({ type: 'error', description: t('intake-error-create') }),
    onSuccess: () => queryClient.invalidateQueries(firmIntakeQuestionKeys.forFirm(firmID)),
  });

  return { createFirmIntakeQuestion };
};

export const useUpdateFirmIntakeQuestion = (firmID: string | undefined | null) => {
  const queryClient = useQueryClient();
  const gqlClient = useGraphqlClient();
  const { t } = useTranslation('firm');

  const updateFirmIntakeQuestion = useMutation({
    mutationFn: (input: UpdateFirmIntakeQuestionInput) => clientUpdateFirmIntakeQuestion(gqlClient, { input }),
    onError: () => openNotification({ type: 'error', description: t('intake-error-update') }),
    onSuccess: () => queryClient.invalidateQueries(firmIntakeQuestionKeys.forFirm(firmID)),
  });

  return { updateFirmIntakeQuestion };
};

export const useDeleteFirmIntakeQuestion = (firmID: string | undefined | null) => {
  const queryClient = useQueryClient();
  const gqlClient = useGraphqlClient();
  const { t } = useTranslation('firm');

  const deleteFirmIntakeQuestion = useMutation({
    mutationFn: (args: { id: string; changeToken: string }) => clientDeleteFirmIntakeQuestion(gqlClient, args),
    onError: () => openNotification({ type: 'error', description: t('intake-error-delete') }),
    onSuccess: () => queryClient.invalidateQueries(firmIntakeQuestionKeys.forFirm(firmID)),
  });

  return { deleteFirmIntakeQuestion };
};

export const useSubmitFirmClientIntakeAnswers = () => {
  const gqlClient = useGraphqlClient();

  const submitFirmClientIntakeAnswers = useMutation({
    mutationFn: async (input: SubmitFirmClientIntakeAnswersInput) => {
      const result = await clientSubmitFirmClientIntakeAnswers(gqlClient, { input });
      if (!result.success) {
        throw new Error('intake answer submission was not successful');
      }
      return result;
    },
  });

  return { submitFirmClientIntakeAnswers };
};
